import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Skeleton,
  Stack,
  Stepper,
  Step,
  StepLabel,
  stepConnectorClasses,
  useMediaQuery,
  useTheme,
  styled,
  Typography,
} from "@mui/material";

// Connector styles
const ColorlibConnector = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: 16,
  "& .line": {
    flex: 1,
    height: 3,
    borderRadius: 1,
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    transition: "background-color 0.3s ease",
  },
  "& .line.active, & .line.completed": {
    backgroundColor: "#BB162B",
  },
}));

const ColorlibStepIconRoot = styled("div")(({ ownerState }) => ({
  backgroundColor: "#ccc",
  zIndex: 1,
  width: 8,
  height: 8,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
  transition: "background-color 0.3s ease, transform 0.3s ease",
  ...(ownerState.active && {
    backgroundColor: "#FFF",
    transform: "scale(3.5)",
  }),
  ...(ownerState.completed && {
    backgroundColor: "#BB162B",
  }),
}));

function ColorlibStepIcon({ active, completed, onClick }) {
  return <ColorlibStepIconRoot ownerState={{ completed, active }} onClick={onClick} />;
}

export default function CarStepper({ StepData, carLink, stepTimings }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [targetStep, setTargetStep] = useState(null);
  const videoRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleStepClick = (clickedStep) => {
    if (videoRef.current) {
      setActiveStep(clickedStep);
      setTargetStep(clickedStep);
      const previousStepTime = stepTimings[activeStep];
      videoRef.current.currentTime = previousStepTime;
      videoRef.current.play();
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      let newStep = 0;
      for (let i = 0; i < stepTimings.length; i++) {
        if (video.currentTime >= stepTimings[i]) newStep = i;
        else break;
      }
      setActiveStep(newStep);

      if (targetStep !== null && video.currentTime >= stepTimings[targetStep]) {
        video.pause();
        setTargetStep(null);
      }
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, [targetStep]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {/* Video Section */}
      <div style={{ position: "relative", width: "100%", height: isSmallScreen ? "50vh" : "100vh" }}>
        {!isLoaded && <Skeleton animation="wave" variant="rectangular" width="100%" height="100%" />}
        <video
          ref={videoRef}
          playsInline
          autoPlay
          loop
          muted
          preload="auto"
          onCanPlayThrough={() => setIsLoaded(true)}
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
        >
          <source src={carLink} type="video/mp4" />
        </video>

        {/* Info Box */}
        <div
          style={{
            position: "absolute",
            top: "7%",
            left: "7%",
            backgroundColor: "#FFF",
            padding: isSmallScreen ? "8px" : "16px",
            maxWidth: isSmallScreen ? "90%" : "400px",
          }}
        >
          <Typography
            variant="subtitle1"
            color="#888"
            letterSpacing={2}
            style={{ fontFamily: "KIAbodyLatinbasic_Regular", fontSize: "clamp(8px, 3vw, 16px)" }}
          >
            EXPLORE
          </Typography>
          <Typography
            style={{
              fontFamily: "KIAbodyLatinbasic_Bold",
              lineHeight: 1.5,
              fontSize: "clamp(10px, 2vw, 20px)",
            }}
          >
            {StepData[activeStep]?.title}
          </Typography>
          <Typography
            style={{
              fontFamily: "KIAbodyLatinbasic_Regular",
              textAlign: "justify",
              fontSize: "clamp(8px, 1.8vw, 16px)",
            }}
          >
            {StepData[activeStep]?.desc}
          </Typography>
        </div>

        {/* Stepper - Full Width */}
        <Box
          sx={{
            position: "absolute",
            bottom: "5%",
            left: 0,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 16px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              position: "relative",
              maxWidth: "none", // Remove maxWidth restriction
            }}
          >
            {/* Background Connector Line - Full Width */}
            <Box
              sx={{
                position: "absolute",
                top: "4px",
                left: "2%", // Reduced from 5% to use more width
                right: "2%", // Reduced from 5% to use more width
                height: 2,
                backgroundColor: "rgba(255,255,255,0.3)",
                zIndex: 1
              }}
            />

            {/* Active Progress Line - Full Width */}
            <Box
              sx={{
                position: "absolute",
                top: "4px",
                left: "2%",
                height: 2,
                backgroundColor: "#BB162B",
                zIndex: 2,
                width: `${(activeStep / (StepData.length - 1)) * 96}%`, // Adjusted for new left/right
                transition: "width 0.3s ease"
              }}
            />

            {StepData.map((step, index) => (
              <Box
                key={index}
                sx={{
                    top: "-10px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                  position: "relative",
                  zIndex: 3,
                  flex: 1,
                  padding: "0 8px",
                  height: "80px", // Fixed height for consistent alignment
                }}
              >

                {/* Step Dot - Bottom section with fixed position */}
                <Box
                  sx={{
                    height: "32px", // Fixed height for dot container
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    top: 0,
                  }}
                >
                  <ColorlibStepIcon
                    active={activeStep === index}
                    completed={activeStep > index}
                    onClick={() => handleStepClick(index)}
                  />
                </Box>
                {/* Step Title - Top section */}
                {!isSmallScreen && (
                  <Box
                    sx={{
                      height: "40px", // Fixed height for title
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#FFF",
                        fontFamily: "KIAbodyLatinbasic_Regular",
                        fontSize: "14px",
                        textAlign: "center",
                        whiteSpace: "wrap",
                        opacity: activeStep === index ? 1 : 0.7,
                        lineHeight: 1.2,
                        maxWidth: "120px",
                      }}
                    >
                      {step.title}
                    </Typography>
                  </Box>
                )}


              </Box>
            ))}
          </Box>
        </Box>
      </div>
    </div>
  );
}